import {
  Box,
  Button,
  Chip,
  Container,
  Paper,
  Stack,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import MailOutlineIcon from '@mui/icons-material/MailOutlined';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import WarehouseOutlinedIcon from '@mui/icons-material/WarehouseOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import { Link as RouterLink } from 'react-router-dom';
import type { SvgIconComponent } from '@mui/icons-material';

const PIPELINE_STEPS: { icon: SvgIconComponent; title: string; body: string }[] = [
  {
    icon: MailOutlineIcon,
    title: 'Email it in',
    body: 'Forward the packing slip, or photograph it on your phone. No forms, no data entry.',
  },
  {
    icon: AutoAwesomeIcon,
    title: 'HXIQ reads it',
    body: 'Line items, quantities, units, and costs are extracted and matched to your catalog.',
  },
  {
    icon: Inventory2OutlinedIcon,
    title: 'Inventory updates',
    body: 'Stock posts to the right bin with a ledger entry pointing back at the original document.',
  },
];

const FEATURES: { icon: SvgIconComponent; title: string; body: string }[] = [
  {
    icon: MenuBookOutlinedIcon,
    title: 'Catalog',
    body: 'Specifications, suppliers, cross-references, and company part numbers. Compatible equipment lists that build themselves from issue history.',
  },
  {
    icon: WarehouseOutlinedIcon,
    title: 'Inventory',
    body: 'Storerooms, bins, lots, and heat numbers. Reservations so two jobs are never promised the same gasket.',
  },
  {
    icon: ReceiptLongOutlinedIcon,
    title: 'Full provenance',
    body: 'Every quantity traces back to the document that produced it — and to what the extractor read on the page.',
  },
  {
    icon: TrendingUpOutlinedIcon,
    title: 'Margin that holds',
    body: 'Markup priced off replacement cost, not a stale average. HXIQ tells you when average cost would lose you money.',
  },
];

function Hero() {
  const theme = useTheme();

  return (
    <Box
      sx={{
        position: 'relative',
        overflow: 'hidden',
        pt: { xs: 8, md: 14 },
        pb: { xs: 10, md: 16 },
        // Radial wash rather than a hard gradient band — keeps the header
        // transparent state readable in both themes.
        background: `radial-gradient(1200px 500px at 20% -10%, ${alpha(
          theme.palette.primary.main,
          theme.palette.mode === 'light' ? 0.14 : 0.22,
        )}, transparent 70%)`,
      }}
    >
      <Container maxWidth="lg">
        <Stack spacing={3} sx={{ maxWidth: 760 }}>
          <Chip
            label="Heat Exchanger IQ"
            size="small"
            sx={{
              alignSelf: 'flex-start',
              fontWeight: 600,
              letterSpacing: '0.04em',
              bgcolor: alpha(theme.palette.primary.main, 0.12),
              color: 'primary.main',
            }}
          />

          <Typography variant="h1">
            Inventory that reads
            <Box component="span" sx={{ color: 'primary.main' }}>
              {' '}
              its own paperwork
            </Box>
          </Typography>

          <Typography variant="h5" component="p" sx={{ color: 'text.secondary', fontWeight: 400, maxWidth: 620 }}>
            Send a packing slip to an inbox. HXIQ turns it into catalog items and stock on the
            shelf — and asks a person only when the document genuinely does not say enough.
          </Typography>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ pt: 1 }}>
            <Button
              component={RouterLink}
              to="/demo"
              size="large"
              variant="contained"
              endIcon={<ArrowForwardIcon />}
            >
              Try the demo
            </Button>
            <Button component={RouterLink} to="/login" size="large" variant="outlined">
              Log in
            </Button>
          </Stack>

          <Typography variant="body2" color="text.secondary">
            No account needed for the demo.
          </Typography>
        </Stack>
      </Container>
    </Box>
  );
}

function Pipeline() {
  const theme = useTheme();

  return (
    <Container maxWidth="lg" sx={{ pb: { xs: 8, md: 12 } }}>
      <Box
        sx={{
          display: 'grid',
          gap: { xs: 2, md: 3 },
          gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
        }}
      >
        {PIPELINE_STEPS.map((step, index) => (
          <Paper
            key={step.title}
            variant="outlined"
            sx={{ p: 3, position: 'relative', height: '100%', borderColor: 'divider' }}
          >
            <Stack spacing={1.5}>
              <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
                <Box
                  sx={{
                    display: 'grid',
                    placeItems: 'center',
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: 'primary.main',
                  }}
                >
                  <step.icon fontSize="small" />
                </Box>
                <Typography
                  variant="overline"
                  sx={{ color: 'text.secondary', fontFamily: 'monospace' }}
                >
                  Step {index + 1}
                </Typography>
              </Stack>
              <Typography variant="h4">{step.title}</Typography>
              <Typography variant="body2" color="text.secondary">
                {step.body}
              </Typography>
            </Stack>
          </Paper>
        ))}
      </Box>
    </Container>
  );
}

function Features() {
  return (
    <Box sx={{ bgcolor: 'background.paper', borderTop: 1, borderBottom: 1, borderColor: 'divider' }}>
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
        <Stack spacing={1.5} sx={{ mb: 5, maxWidth: 640 }}>
          <Typography variant="h2">Built for the crib, not the spreadsheet</Typography>
          <Typography variant="body1" color="text.secondary">
            Heat exchanger work has its own vocabulary — heat numbers, BWG, retube kits,
            customer-supplied material. HXIQ models it the way the shop already thinks about it.
          </Typography>
        </Stack>

        <Box
          sx={{
            display: 'grid',
            gap: { xs: 2, md: 3 },
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
          }}
        >
          {FEATURES.map((feature) => (
            <Stack key={feature.title} direction="row" spacing={2} sx={{ alignItems: 'flex-start' }}>
              <Box sx={{ color: 'primary.main', pt: 0.25 }}>
                <feature.icon />
              </Box>
              <Stack spacing={0.75}>
                <Typography variant="h5">{feature.title}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {feature.body}
                </Typography>
              </Stack>
            </Stack>
          ))}
        </Box>
      </Container>
    </Box>
  );
}

function ClosingCta() {
  return (
    <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
      <Stack spacing={3} sx={{ alignItems: 'center', textAlign: 'center' }}>
        <Typography variant="h2" sx={{ maxWidth: 640 }}>
          See a packing slip become inventory
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 520 }}>
          The demo runs the real pipeline against a sandbox storeroom. Drop in a PDF or use one of
          the samples.
        </Typography>
        <Button
          component={RouterLink}
          to="/demo"
          size="large"
          variant="contained"
          endIcon={<ArrowForwardIcon />}
        >
          Try the demo
        </Button>
      </Stack>
    </Container>
  );
}

export function LandingPage() {
  return (
    <>
      <Hero />
      <Pipeline />
      <Features />
      <ClosingCta />
    </>
  );
}
